import * as _ from "lodash";

import { ExtensionBase } from "../extensionBase";
import { UiUtils } from "../../common/uiUtils";
import { viewerOptions } from "../../viewer";
import { ViewerUtils } from "../../common/viewerUtils";
import * as SmartDrawings from "../../smartDrawings";
import ExternalData = SmartDrawings.ExternalData;
import { QuickFilterUtils } from "../filter/quickFilterUtils";

export class GroupSelectionExtension extends ExtensionBase {
    static extensionName = "GroupSelectionExtension";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private groupSelectionButton: Autodesk.Viewing.UI.Button;

    private selectionCategory: string;
    private selectionProperty: string;
    private selected: number[];

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
    }

    static registerExtension() {
        super.registerExtension(GroupSelectionExtension.extensionName, GroupSelectionExtension);
    }

    load(): boolean {
        console.log(`${GroupSelectionExtension.extensionName} is loaded!`);
        return super.load();
    };

    unload(): boolean {
        return super.unload();
    };

    async onToolbarCreated() {
        if (this.checkUda()) {
            this.groupSelectionButton = UiUtils.createButton({
                id: "SmartDrawings.GroupSelectionButton",
                tooltip: "Select groups",
                iconClass: "ion-md-filing",
                onClick: () => {
                    if (this.groupSelectionButton.getState() === Autodesk.Viewing.UI.Button.State.INACTIVE)
                        this.startSelecting();
                    else if (this.groupSelectionButton.getState() === Autodesk.Viewing.UI.Button.State.ACTIVE) {
                        this.stopSelecting();
                    }
                }
            });

            this.groupSelectionButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);

            this.subToolbar = this.getControlGroup("SmartDrawings.ExtDataExtensions");
            this.subToolbar.addControl(this.groupSelectionButton);

            this.viewer.addEventListener(SmartDrawings.SdEvents.PARAMS_TO_DBIDS_MAPPED,
                this.onExternalDataLoaded.bind(this));
        }
    }

    onExternalDataLoaded() {
        this.groupSelectionButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
        this.viewer.removeEventListener(SmartDrawings.SdEvents.PARAMS_TO_DBIDS_MAPPED,
            this.onExternalDataLoaded.bind(this));
    }

    private async startSelecting() {
        const elements = this.viewer.getSelection();
        if (elements.length > 0)
            this.selected = elements;

        this.groupSelectionButton.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
        this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
    }

    private stopSelecting() {
        this.groupSelectionButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
        this.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
        this.selected = [];
    }

    selectionListener = async (nodes) => {
        const elements = nodes.dbIdArray;
        if (elements.length > 0) {
            const props = viewerOptions.externalData.propertyValues;
            let ids = [];
            let earlierSelectionFound = false;
            for (let element of elements) {
                if (!_.includes(this.selected, element)) {
                    const mappedId = await this.findMappedId(element);
                    if (mappedId) {
                        if (mappedId in props) {
                            let value = props[mappedId][this.selectionProperty];
                            let nodeIds = await QuickFilterUtils.searchExternalData("=",
                                value,
                                true,
                                this.selectionCategory,
                                this.selectionProperty);
                            ids = _.union(ids, nodeIds);
                        } else
                            UiUtils.notifyWarning("Object grouping information does not exist.");
                    }
                }
                else if (!earlierSelectionFound) {
                    ids = _.union(ids, this.selected);
                    earlierSelectionFound = true;
                }
            }

            if (ids.length > 0) {
                this.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
                this.viewer.select(ids);
                this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
                this.selected = ids;
            }
        }
    }

    private checkUda(): boolean {
        const { userDefinedAttributes } = viewerOptions;
        if (userDefinedAttributes == null)
            return false;

        if (userDefinedAttributes["groupCategory"] && userDefinedAttributes["groupProperty"]) {
            this.selectionCategory = userDefinedAttributes["groupCategory"];
            this.selectionProperty = userDefinedAttributes["groupProperty"];

            return true;
        }

        return false;
    }

    private async findMappedId(nodeId: number) {
        var extData: ExternalData = viewerOptions.externalData;
        const mappedId = _.keys(extData.paramsToDbIds).filter(id => extData.paramsToDbIds[id] === nodeId);
        if (mappedId.length > 0) {
            return mappedId[0];
        }

        // Or try to find data in the parent.
        const parentId = await ViewerUtils.getNodeParent(nodeId, this.viewer);
        if (parentId != null) {
            return await this.findMappedId(parentId);
        }

        return null;
    }
}